import { CorrespondenceInputType } from "../enums/enums";
import { ICorrespondence } from "../interfaces/Correspondence";
import { IParamResponseTime } from "../redux/types/types";
import { calculateTrafficLightPercentage } from "../components/correspondence-module/pqrs/TrafficLight";

/**
 * Verifica si una correspondencia cumple con los valores de filtro aplicados.
 * Aplica filtros por semáforo, tipo de entrada, rango de fechas y estado.
 *
 * @param correspondence - La correspondencia a evaluar.
 * @param paramResponseTime - Parámetros de tiempo de respuesta.
 * @param rangeDate - Rango de fechas para filtrar.
 * @param state - Estado de la correspondencia para filtrar.
 * @param trafficLight - Nivel del semáforo para filtrar.
 * @param inputType - Tipo de entrada para filtrar.
 * @param paramsProcedure - Parámetros adicionales relacionados con los procedimientos.
 * @returns `true` si la correspondencia cumple con los filtros, `false` en caso contrario.
 */
export const verifyFilterValues = (
    correspondence: ICorrespondence,
    paramResponseTime: IParamResponseTime,
    rangeDate: { startDate: Date; endDate: Date } | null,
    state?: string | null,
    trafficLight?: number | undefined,
    inputType?: any,
    paramsProcedure?: any
): boolean => {
    if (!correspondence.createAt) {
        return false;
    }

    if (trafficLight && !filterByTrafficLight(correspondence, paramResponseTime, trafficLight, paramsProcedure)) {
        return false;
    }

    if (inputType && !filterByInputType(correspondence, inputType)) {
        return false;
    }

    if (rangeDate && !filterByDateRange(correspondence.createAt, rangeDate)) {
        return false;
    }

    if (state && !filterByState(correspondence.internalState, state)) {
        return false;
    }

    return true;
};

/**
 * Filtra una correspondencia según el nivel del semáforo.
 * Calcula el porcentaje de tiempo transcurrido y compara con el nivel del semáforo.
 *
 * @param correspondence - La correspondencia a evaluar.
 * @param paramResponseTime - Parámetros de tiempo de respuesta.
 * @param trafficLight - Nivel del semáforo para filtrar.
 * @param paramsProcedure - Parámetros adicionales relacionados con los procedimientos.
 * @returns `true` si la correspondencia coincide con el nivel del semáforo, `false` en caso contrario.
 */
const filterByTrafficLight = (
    correspondence: ICorrespondence,
    paramResponseTime: IParamResponseTime,
    trafficLight: number,
    paramsProcedure?: any
): boolean => {
    if (
        correspondence.inputType !== CorrespondenceInputType.ER &&
        correspondence.inputType !== CorrespondenceInputType.IR &&
        correspondence.inputType !== CorrespondenceInputType.OR
    ) {
        return false;
    }

    const { percentage } = calculateTrafficLightPercentage(
        correspondence,
        correspondence.communicationType,
        paramResponseTime,
        paramsProcedure || []
    );

    if (percentage === null || isNaN(percentage)) {
        return trafficLight === 5;
    }

    const trafficLightMapping: Record<number, boolean> = {
        1: percentage <= 60,
        2: percentage > 60 && percentage <= 70,
        3: percentage > 70 && percentage < 100,
        4: percentage >= 100,
    };

    return trafficLightMapping[trafficLight] ?? false;
};

/**
 * Filtra una correspondencia según el tipo de entrada.
 *
 * @param correspondence - La correspondencia a evaluar.
 * @param inputType - Tipo de entrada para filtrar.
 * @returns `true` si la correspondencia coincide con el tipo de entrada, `false` en caso contrario.
 */
const filterByInputType = (
    correspondence: ICorrespondence,
    inputType: any
): boolean => {
    return correspondence.inputType === inputType;
};

/**
 * Filtra una correspondencia según un rango de fechas.
 * Compara la fecha de creación de la correspondencia con el rango proporcionado.
 *
 * @param createAt - Fecha de creación de la correspondencia.
 * @param rangeDate - Rango de fechas para filtrar.
 * @returns `true` si la correspondencia está dentro del rango de fechas, `false` en caso contrario.
 */
const filterByDateRange = (
    createAt: any,
    rangeDate: { startDate: Date; endDate: Date }
): boolean => {
    const startDate = new Date(rangeDate.startDate);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(rangeDate.endDate);
    endDate.setHours(23, 59, 59, 999);
    const createDate = createAt.toDate();

    return createDate >= startDate && createDate <= endDate;
};

/**
 * Filtra una correspondencia según su estado.
 *
 * @param internalState - Estado actual de la correspondencia.
 * @param state - Estado para filtrar.
 * @returns `true` si la correspondencia coincide con el estado proporcionado, `false` en caso contrario.
 */
const filterByState = (
    internalState: string | undefined,
    state: string
): boolean => {
    return internalState === state.trim();
};
